import React, { Suspense } from 'react';
import dynamic from 'next/dynamic';
import { Nav } from '@components/Nav';
import { Loading } from '@components/Loading';
import { ConnectOrSignIn } from '@components/SignIn';
import { Container } from '@components/Container';
import { Head, HeadProps } from '@components/Head';
import { UserType } from '@utils/types';
import { UserProvider, useUserContext } from '@providers/UserProvider';
import { GradientPanel } from '@components/Panel';
import { LogoGradientLogo } from '@components/LogoGradient';
import { SolUserProvider } from '@providers/SolUserProvider';
import { PrivyUserProvider } from '@providers/PrivyUserProvider';

export interface AuthLayoutProps {
  children: React.ReactNode;
  userType?: UserType;
  coloredBorder?: boolean;
  navSelected?: string;
  navWhiteBackground?: boolean;
  transparentBackground?: boolean;
  showContent?: boolean;
  fullWidth?: boolean;
  hideNav?: boolean;
  wrapperStyles?: React.CSSProperties;
  headProps?: HeadProps;
  skipHead?: boolean;
  hideNavDropdown?: boolean;
}

export function AuthLayout(props: AuthLayoutProps) {
  return (
    <>
      {!props.skipHead && <Head {...props.headProps} />}
      <UserProvider>
        <PrivyUserProvider>
          <SolUserProvider>
            <div className="min-h-screen bg-slate-50" style={props.wrapperStyles}>
              <AuthLayoutContent {...props} />
            </div>
          </SolUserProvider>
        </PrivyUserProvider>
      </UserProvider>
      <Suspense>
        <Crisp />
      </Suspense>
    </>
  );
}

function AuthLayoutContent(props: AuthLayoutProps) {
  const { user, fetchedInitialUser } = useUserContext();

  const showLogin = fetchedInitialUser && !props.showContent && !user?.address;
  const transparentBackground = showLogin || props.transparentBackground;

  return (
    <>
      {!props.hideNav && (
        <div className="relative z-20">
          <Nav
            userType={props.userType}
            coloredBorder={props.coloredBorder && !transparentBackground}
            transparentBackground={transparentBackground}
            selected={props.navSelected}
            hideNavDropdown={props.hideNavDropdown}
          />
        </div>
      )}
      {props.showContent || user?.address ? (
        props.fullWidth ? (
          props.children
        ) : (
          <Container>{props.children}</Container>
        )
      ) : fetchedInitialUser ? (
        <LogIn />
      ) : (
        <Loading />
      )}
    </>
  );
}

function LogIn() {
  return (
    <div className="flex justify-center">
      <div className="mt-32 px-6">
        <GradientPanel>
          <div className="p-6 md:p-16">
            <LogoGradientLogo className="mx-auto h-10 w-auto sm:h-12" />

            <div className="mt-3 text-center text-xl font-extrabold leading-snug sm:text-3xl">
              Welcome to ShareMint
            </div>
            <div className="mt-2 text-center sm:text-lg">Sign in to continue</div>
            <div className="mt-8">
              <ConnectOrSignIn />
            </div>
          </div>
        </GradientPanel>
      </div>
    </div>
  );
}

const Crisp = dynamic(() => import('../components/Crisp'), { ssr: false });
